import axios from 'axios';
import { store } from '@/store/store';
import { type ClassValue, clsx } from 'clsx'
import { DateTime } from 'luxon';
import { twMerge } from 'tailwind-merge'
import type { RouteLocationNormalizedLoaded } from 'vue-router'
import { update as updateIntercom } from '@intercom/messenger-js-sdk'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function getParam(route?: RouteLocationNormalizedLoaded): string {
  if (route && route.params && 'domain' in route.params) {
    return route.params.domain as string;
  }
  return '';
}

export function clearLocalStorage() {
    localStorage.removeItem('doctor');
    localStorage.removeItem('appointment');
    localStorage.removeItem('reschedule');
}

const formatTimePart = (part: number): string => {
  return part.toString().padStart(2, '0');
};

export function bucketTime(time: string): string {
  const [hour, minute] = time.split(':').map(Number);
  const minuteBucket = minute - (minute % 15);
  return `${formatTimePart(hour)}:${formatTimePart(minuteBucket)}`;
}

/**
 * Delay for a given number of milliseconds
 * @param ms - The number of milliseconds to delay
 * @returns A promise that resolves after the delay
 */
export function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function updateAxiosAuthHeader(token: string) {
  axios.defaults.headers['authorization'] = `Bearer ${token}`;
}

/**
 * Convert a date string from the server to a local date
 * @param dateStr - The date string to convert in the format "yyyy-MM-dd HH:mm:ss"
 * @returns The local date
 */
export function convertServerDateToLocalDate(dateStr: string): Date {
    const date = DateTime.fromFormat(dateStr, "yyyy-MM-dd HH:mm:ss", {
        zone: "America/Vancouver",
    })
    return date.toJSDate()
}

/**
 * Given the appointment ID, returns the params which should be sent to intercom
 * for that appointment.
 * 
 * @param appointmentId - The appointment ID
 * @returns Params the should be attached to the user object in intercom
 */
export function addIntercomAppointmentParams(appointmentId: Nullable<string>): {
  appointment_id: Nullable<string>,
  panel_link: Nullable<string>,
} {
  const mustResetAppointmentParams = appointmentId === null || appointmentId === '';

  if (mustResetAppointmentParams) {
    // to reset the intercom params, we must set them to null
    return {
      appointment_id: null,
      panel_link: null,
    }
  }
  
  return {
      appointment_id: appointmentId,
      panel_link: `https://aveecloud.com/admn/appointment_report?appID=${appointmentId}`,
  }
}

export function isToday(date: Date): boolean {
    const now = new Date();
    return now.toDateString() === date.toDateString();
}

export function isTomorrow(date: Date): boolean {
    const tomorrow = new Date(new Date().setDate(new Date().getDate() + 1));
    return date.toDateString() === tomorrow.toDateString();
}

export const formatDateTime = (dateStr: string) => { 
  const date = convertServerDateToLocalDate(dateStr);
  
  if (date) {
      const now = new Date();
      const diffInMs = now.getTime() - date.getTime();
      const diffInDays = Math.abs(Math.floor(diffInMs / (1000 * 60 * 60 * 24)));
      
      let day = '';
      if (isToday(date)) {
          day = 'Today';
      } else if (isTomorrow(date)) {
          day = 'Tomorrow';
      } else if (diffInDays <= 7) {
          // within 7 days (past or future): show weekday only
          day = date.toLocaleString('en-US', { weekday: 'short' });
      } else if (diffInDays <= 180) { // approximately 6 months
          // beyond 7 days but within 6 months: show month/day
          day = date.toLocaleString('en-US', { month: 'short', day: 'numeric' });
      } else {
          // beyond 6 months: show month/day/year
          day = date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      }
      
      const [time, ampm] = date.toLocaleString('en-US', { hour: 'numeric', minute: 'numeric' }).split(' ');

      return `${day}, ${time} ${ampm}`;
  }
  return '';
};

export function mapStatus(status: string, shortForm: boolean = false) {

  let output: string = '';

  switch (status) {
      case 'Reserved':
          output = 'Reserved';
          break;
      case 'WinRx':
      case 'Waiting':
          output = 'Waiting';
          break;
      case 'No Show':
          output = 'No Show';
          break;
      case 'Cancelled':
      case 'Canceled':
          output = shortForm ? 'Cancelled' : 'Cancelled by doctor';
          break;
      case 'Deleted':
          output = shortForm ? 'Cancelled by you' : 'Cancelled by patient';
          break;
      case 'Billed':
      case 'Finished':
          output = shortForm ? 'Visited' : 'Successful Visit';
          break;
      case 'ReservationExpired':
          output = 'Expired';
          break;
      case 'Changed':
          output = 'Changed';
          break;
      default:
          output = status;
  }

  return output;
}

export function diff_hours(dt2, dt1) {

  let diff =(dt2.getTime() - dt1.getTime()) / 1000;
  diff /= (60 * 60);

  return Math.abs(Math.round(diff));

}

export function getRelativeTime(targetDate: Date): string {
  const now = new Date();
  const diffMs = targetDate.getTime() - now.getTime();
  const diffMinutes = Math.round(diffMs / (1000 * 60));
  const diffHours = Math.round(diffMs / (1000 * 60 * 60));
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 0) {
    return 'in the past';
  } else if (diffMinutes < 60) {
    if (diffMinutes === 1) return 'in 1 minute';
    return `in ${diffMinutes} minutes`;
  } else if (diffHours < 24) {
    if (diffHours === 1) return 'in 1 hour';
    return `in ${diffHours} hours`;
  } else {
    if (diffDays === 1) return 'in 1 day';
    return `in ${diffDays} days`;
  }
}

/**
 * Formats a future deadline according to rules:
 * - if less than 1 hour: "within <minutes> minutes"
 * - if less than 24 hours: "within <hours> h and <minutes> m" (omit minutes when 0)
 * - if 24 hours or more: "by Aug 14"
 */
export function formatDeadline(deadline: string | Date): string {
  const deadlineDate = typeof deadline === 'string' ? new Date(deadline) : deadline;
  const now = new Date();
  let diffMs = deadlineDate.getTime() - now.getTime();

  if (isNaN(deadlineDate.getTime())) {
    return '';
  }

  // Treat past or immediate deadlines as "within 1 minute"
  if (diffMs <= 0) {
    return 'within **1 minute**';
  }

  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;

  if (diffMs < hourMs) {
    const minutes = Math.max(1, Math.floor(diffMs / minuteMs));
    return `within **${minutes} minutes**`;
  }

  if (diffMs < dayMs) {
    const hours = Math.floor(diffMs / hourMs);
    const remainingMs = diffMs - hours * hourMs;
    const minutes = Math.floor(remainingMs / minuteMs);
    return minutes > 0
      ? `within **${hours} h and ${minutes} m**`
      : `within **${hours} h**`;
  }

  const by = deadlineDate.toLocaleString('en-US', { month: 'short', day: 'numeric' });
  return `by **${by}**`;
}

export function choosePatient (id: string) {

  storeSelectedPatient(id);
  storeSource();
  
  localStorage.setItem('appointment', JSON.stringify(store.appointment));
  localStorage.removeItem('reschedule');

  // update intercom with patient selection
  const selectedMember = store.members.find((member) => member.pid === id);
  if (selectedMember) {
    const patientIdExists = selectedMember.pid !== '';
    const bookedAppointment = store.app;
    const patientWantsToBookANewAppointment = (
      // if they're not editing an appointment, bookedAppointment will be empty
      // and it means we are booking a new appointment
      !bookedAppointment?.appID ||
      // they might've gone through the editing process, but changed the patient.
      // in this case, we need to reset the intercom params.
      bookedAppointment?.pid !== selectedMember.pid
    );

    if (patientIdExists && patientWantsToBookANewAppointment) {
      updateIntercom({
        patient_id: selectedMember.pid,
        ...addIntercomAppointmentParams(null),
      });
    }
  }
}

function storeSelectedPatient(id: string) {
  const selectedMember = store.members.find((member) => member.pid === id);
  if (selectedMember) {
    store.appointment.pid = selectedMember.pid;
  }
}

function storeSource() {
  const source = localStorage.getItem('source');
  if (source) {
      store.appointment.source = source;
  }
}

export function formatPhoneNumber(phone: string) {
  if (!phone) {
      return '';
  }
  const digits = phone.replace(/\D/g, '');
  const parts = digits.match(/.{1,3}/g);
  return parts ? `+1 (${parts[0]}) ${parts[1]}-${parts[2]}${parts[3]}` : '';
}

export function logout(route?: RouteLocationNormalizedLoaded) {
  localStorage.removeItem('token');
  clearLocalStorage();
  axios.defaults.headers['authorization'] = '';

  let redirectUrl = '/'
  if (route && route.params && 'domain' in route.params) {
      redirectUrl += route.params.domain;
  }
  store.logout();

  window.location.href = redirectUrl;
}

export function generateReturnURLs(appointmentId: string, route: RouteLocationNormalizedLoaded) {
    const baseURL = window.location.href.replace(route.fullPath, '');

    const parts = [baseURL];

    if (!!route.params.domain) {
        parts.push(route.params.domain as string);
    }

    parts.push('profile');
    parts.push(route.params.patientId as string);

    const successURL = [...parts, 'success'];
    const errorURL = [...parts, 'failed_pay'];

    return {
        appID: appointmentId,
        successBackUrl: successURL.join('/'),
        errorBackUrl: errorURL.join('/')
    }
}
    